import { createSelector } from '@reduxjs/toolkit';

import UniqueId from '../../services/uid/UniqueId';
import { AppState } from '../reducers/root';
import { ContentEntityType, OpenContentEntity } from '../slices/content';

import { getCategoryState } from './category';

const getContentEntities = (state: AppState) => state.content.entities;
const getContentOrder = (state: AppState) => state.content.order;
const getContentActive = (state: AppState) => state.content.active;
const getContentLastUsed = (state: AppState) => state.content.lastUsed;

export const getContentEntity = (
  state: AppState,
  id: UniqueId
): OpenContentEntity => getContentEntities(state)[id];

export const getAllContentEntityIds = (state: AppState): UniqueId[] =>
  getContentOrder(state);

export const getAllContentEntities = createSelector(
  [getContentOrder, getContentEntities, (state: AppState) => state.category],
  (order, entities, categoryState) =>
    order.map(id => {
      const entity = entities[id];
      if (entity.type === ContentEntityType.CATEGORY) {
        const category = categoryState[entity.content];
        const isLinksGraph = !!category?.category.source.inParams?.linksGraph;
        return { ...entity, canPin: !isLinksGraph };
      }
      return { ...entity, canPin: false };
    })
);

export const getActiveContentTab = (state: AppState): UniqueId | null =>
  getContentActive(state);

const getLastUsedCategoryEntities = createSelector(
  [getContentLastUsed, getContentEntities],
  (lastUsed, entities) =>
    lastUsed
      .map(id => entities[id])
      .filter(entity => entity.type === ContentEntityType.CATEGORY)
);

const findLastUsedDisplayModes = createSelector(
  [
    (state: AppState) => state,
    (_state: AppState, category: string) => category,
    (_state: AppState, _category: string, displayMode: string) => displayMode,
    (
      _state: AppState,
      _category: string,
      _displayMode: string,
      isDocLinks?: boolean
    ) => isDocLinks,
    getLastUsedCategoryEntities,
  ],
  (state, category, displayMode, isDocLinks, categoryEntities) =>
    categoryEntities.filter(entity => {
      const categorySource = getCategoryState(state, entity.content).source;
      return (
        categorySource.category === category &&
        categorySource.displayMode === displayMode &&
        ((isDocLinks && categorySource.inParams?.linksGraph) ||
          (!isDocLinks && !categorySource.inParams?.linksGraph))
      );
    })
);

export const hasDisplayModeContentTab = (
  state: AppState,
  category: string,
  displayMode: string,
  isDocLinks?: boolean
): boolean =>
  findLastUsedDisplayModes(state, category, displayMode, isDocLinks).length > 0;

export const getLastUsedDisplayModeContentTab = (
  state: AppState,
  category: string,
  displayMode: string,
  isDocLinks?: boolean
): UniqueId =>
  findLastUsedDisplayModes(state, category, displayMode, isDocLinks)[0].id;

export const getActiveContentTabTitle = createSelector(
  [getContentActive, getContentEntities],
  (activeId, entities): string | null =>
    activeId ? entities[activeId].title : null
);

export const getContentByEntityId = createSelector(
  [
    getContentEntities,
    (_state: AppState, entityId: UniqueId): UniqueId => entityId,
  ],
  (entities, entityId) =>
    Object.entries(entities).find(
      ([, { content: contentId }]) => contentId === entityId
    )?.[0]
);

// Don't use createSelector
export const getContextIds = (state: AppState): UniqueId[] => {
  const order = getContentOrder(state);
  const entities = getContentEntities(state);
  return order.map(contentId => entities[contentId].content);
};

export const getActiveCategoryId = createSelector(
  [
    (state: AppState) => state.dictionary.order.at(-1),
    getContentActive,
    getContentEntities,
  ],
  (lastDictionaryCategoryId, activeContentId, contentEntities) => {
    const tabCategoryId =
      activeContentId && contentEntities[activeContentId].content;

    return lastDictionaryCategoryId || tabCategoryId;
  }
);

export const getContextActiveTabs = createSelector(
  [getContentActive, getContentEntities, (state: AppState) => state.category],
  (
    activeId,
    entities,
    categoryState
  ): Record<UniqueId, UniqueId> | undefined =>
    activeId && entities[activeId].type === ContentEntityType.CATEGORY
      ? categoryState[entities[activeId].content].activeTabs
      : undefined
);

export const getBalanceEntityId = createSelector(
  [getContentEntities, (_state: AppState, id: UniqueId) => id],
  (entities, id): UniqueId | undefined =>
    Object.values(entities).find(
      ({ content, type }) =>
        content === id && type === ContentEntityType.BALANCE_FORM
    )?.id
);

export const getPinnedTabs = createSelector(
  [getContentOrder, getContentEntities, (state: AppState) => state.category],
  (order, entities, categoryState) =>
    order.flatMap(id => {
      const entity = entities[id];
      if (!entity.pinned) return [];
      const {
        source: { category, displayMode, inParams },
      } = categoryState[entity.content].category;
      return [{ category, displayMode, inParams }];
    })
);
